import {ContentContainer} from '@coolcolduk/ui-mui';
import {useMessage} from '@coolcolduk/ui-mui';
import {Button, CircularProgress, Stack, TextField, Typography} from '@mui/material';
import {useCallback, useState} from 'react';
import {z} from 'zod';
import PageHeader from '../../../components/PageHeader';
import {PrivateRouteProps} from '../../../config/routeType';
import DropdownModule from '../../../atoms/dropdownModule/DropdownModule';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import {useCreateSeedQuestion} from '../../../hooks/seedQuestion/useCreateSeedQuestion';
import {SeedQuestionSource} from '@cp/model';

// Schema for seed question JSONL
const SeedQuestionSchema = z.object({
  questionText: z.string(),
  topic: z.string(),
  options: z.array(z.string()),
  answer: z.string(),
  source: z.string(),
  status: z.string().optional(),
  createdAt: z.string().optional(),
});

type SeedQuestionInput = z.infer<typeof SeedQuestionSchema>;

function parseSeedQuestionSource(source: string): SeedQuestionSource {
  if (Object.values(SeedQuestionSource).includes(source as SeedQuestionSource)) {
    return source as SeedQuestionSource;
  }
  return SeedQuestionSource.OAK; // Default to OAK if invalid
}

interface FailedQuestion {
  data: unknown;
  error: string;
}

export default function AdminSeedQuestionImportPage(_props: PrivateRouteProps) {
  const [jsonlText, setJsonlText] = useState('');
  const [moduleId, setModuleId] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [failedQuestions, setFailedQuestions] = useState<FailedQuestion[]>([]);
  const {showMessage} = useMessage();
  const {examModules, loading: modulesLoading} = useListExamModules();
  const {createSeedQuestion, loading: createLoading} = useCreateSeedQuestion();

  const parseJsonl = useCallback((text: string): {valid: SeedQuestionInput[]; failed: FailedQuestion[]} => {
    const lines = text.split('\n').filter((line) => line.trim());
    const valid: SeedQuestionInput[] = [];
    const failed: FailedQuestion[] = [];

    lines.forEach((line, index) => {
      try {
        const parsed: unknown = JSON.parse(line);
        const validated = SeedQuestionSchema.safeParse(parsed);
        if (validated.success) {
          valid.push(validated.data);
        } else {
          failed.push({
            data: parsed,
            error: `Line ${index + 1}: ${validated.error.issues.map((e) => `${e.path.join('.')}: ${e.message}`).join(', ')}`,
          });
        }
      } catch (error) {
        failed.push({
          data: line,
          error: `Line ${index + 1}: ${error instanceof Error ? error.message : 'Invalid JSON'}`,
        });
      }
    });

    return {valid, failed};
  }, []);

  const handleImport = useCallback(async () => {
    if (!moduleId) {
      showMessage('Please select a module', 6000, 'error');
      return;
    }

    if (!jsonlText.trim()) {
      showMessage('Please paste seed questions in JSONL format', 6000, 'error');
      return;
    }

    setImporting(true);
    setFailedQuestions([]);

    try {
      // Parse and validate all questions first
      const {valid: questions, failed: parseFailed} = parseJsonl(jsonlText);
      const importFailed: FailedQuestion[] = [...parseFailed];

      // Import each valid question
      let successCount = 0;
      for (const question of questions) {
        try {
          const source = parseSeedQuestionSource(question.source);
          await createSeedQuestion(
            moduleId,
            question.questionText,
            question.topic,
            question.options,
            question.answer,
            source,
          );
          successCount++;
        } catch (error) {
          importFailed.push({
            data: question,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      }

      if (importFailed.length > 0) {
        setFailedQuestions(importFailed);
        showMessage(
          `Imported ${successCount} seed questions. ${importFailed.length} failed. See errors below.`,
          8000,
          'warning',
        );
      } else {
        showMessage(`Successfully imported ${successCount} seed questions`, 6000, 'success');
        setJsonlText('');
      }
    } catch (error) {
      showMessage(error instanceof Error ? error.message : 'Failed to process seed questions', 6000, 'error');
    } finally {
      setImporting(false);
    }
  }, [jsonlText, moduleId, parseJsonl, createSeedQuestion, showMessage]);

  return (
    <ContentContainer data-testid="admin-seed-question-import-page">
      <PageHeader title="Seed Question Import" />

      <Stack spacing={3}>
        <Typography variant="body1" sx={{color: '#666'}}>
          Paste seed questions in JSONL format. Each line should be a JSON object representing a seed question.
        </Typography>

        <DropdownModule
          examModules={examModules || []}
          moduleId={moduleId}
          onChange={setModuleId}
          disabled={modulesLoading}
          autoSelectFirst={true}
        />

        <TextField
          fullWidth
          multiline
          rows={15}
          label="Seed Questions (JSONL Format)"
          placeholder="Paste seed questions in JSONL format here. Each line should be a JSON object with: questionText, topic, options, answer, source, status (optional), createdAt (optional)"
          value={jsonlText}
          onChange={(e) => setJsonlText(e.target.value)}
          helperText="Each line should be a minified JSON object representing a seed question."
        />

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing || createLoading || !jsonlText.trim() || !moduleId}
            sx={{
              bgcolor: '#1B263B',
              '&:hover': {bgcolor: '#1B263B', opacity: 0.8},
              minWidth: 150,
            }}
          >
            {importing || createLoading ? <CircularProgress size={20} color="inherit" /> : 'Import Seed Questions'}
          </Button>
        </Stack>

        {failedQuestions.length > 0 && (
          <Stack spacing={2} sx={{mt: 2}}>
            <Typography variant="h6" color="error">
              Validation Errors ({failedQuestions.length})
            </Typography>
            {failedQuestions.slice(0, 10).map((failed) => (
              <Stack
                key={`failed-${JSON.stringify(failed.data)}-${failed.error}`}
                spacing={1}
                sx={{p: 2, border: '1px solid', borderColor: 'error.main', borderRadius: 1}}
              >
                <Typography variant="subtitle2" color="error">
                  {failed.error}
                </Typography>
                <Typography variant="body2" component="pre" sx={{overflow: 'auto', fontSize: '0.75rem'}}>
                  {JSON.stringify(failed.data, null, 2)}
                </Typography>
              </Stack>
            ))}
            {failedQuestions.length > 10 && (
              <Typography variant="body2" color="text.secondary">
                ... and {failedQuestions.length - 10} more errors
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </ContentContainer>
  );
}
